import { useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";
import { Mail, Phone, MapPin, Clock } from "lucide-react";

export default function Contact() {
  const { settings } = useSettings();
  useSEO({
    title: "Contact",
    description: "Get in touch with BOLAWPRESS for custom quotes, project inquiries, or general questions.",
    breadcrumbs: [{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }],
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", details: "" });

  const emails = (settings?.contact_emails || []).filter(Boolean);
  const phones = (settings?.phone_numbers || []).filter(Boolean);
  const address = settings?.physical_address || "";
  const hours = settings?.business_hours || "";
  const mapsUrl = settings?.google_maps_embed_url || "";
  const contactText = settings?.contact_page_text || "";

  const hasContactInfo = emails.length + phones.length > 0 || address || hours;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        product_type: "general",
        contact_name: f.name, contact_email: f.email, contact_phone: f.phone,
        subject: f.subject || "Contact form",
        details: f.details, dimensions: "", material: "", finishing: "", quantity: 0,
      });
      setSent(true);
      toast.success("Message sent — we'll respond within one business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Please try again.");
    } finally { setSubmitting(false); }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-16">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Get in Touch</p>
      <h1 className="font-serif text-5xl md:text-7xl mt-4">Contact.</h1>
      {contactText && <p className="mt-6 text-[#555] max-w-2xl leading-relaxed whitespace-pre-line" data-testid="contact-intro">{contactText}</p>}

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Contact info column — only render when we have something */}
        {hasContactInfo && (
          <aside className="md:col-span-4 space-y-8" data-testid="contact-info">
            {emails.length > 0 && (
              <div>
                <Mail size={20} strokeWidth={1.5} className="text-[#C5A059]" />
                <p className="mt-3 text-[10px] font-mono tracking-[0.3em] uppercase text-[#888]">Email</p>
                {emails.map(e => <p key={e} className="mt-1"><a href={`mailto:${e}`} className="text-[#0a0a0a] hover:text-[#C5A059]">{e}</a></p>)}
              </div>
            )}
            {phones.length > 0 && (
              <div>
                <Phone size={20} strokeWidth={1.5} className="text-[#C5A059]" />
                <p className="mt-3 text-[10px] font-mono tracking-[0.3em] uppercase text-[#888]">Phone</p>
                {phones.map(p => <p key={p} className="mt-1"><a href={`tel:${p.replace(/\s+/g,'')}`} className="text-[#0a0a0a] hover:text-[#C5A059]">{p}</a></p>)}
              </div>
            )}
            {address && (
              <div>
                <MapPin size={20} strokeWidth={1.5} className="text-[#C5A059]" />
                <p className="mt-3 text-[10px] font-mono tracking-[0.3em] uppercase text-[#888]">Address</p>
                <p className="mt-1 whitespace-pre-line text-[#0a0a0a]">{address}</p>
              </div>
            )}
            {hours && (
              <div>
                <Clock size={20} strokeWidth={1.5} className="text-[#C5A059]" />
                <p className="mt-3 text-[10px] font-mono tracking-[0.3em] uppercase text-[#888]">Hours</p>
                <p className="mt-1 whitespace-pre-line text-[#0a0a0a]">{hours}</p>
              </div>
            )}
          </aside>
        )}

        {/* Form — always shown */}
        <div className={hasContactInfo ? "md:col-span-8" : "md:col-span-12 max-w-2xl"}>
          {sent ? (
            <div className="border border-[#E5E5E5] p-10" data-testid="contact-sent">
              <h2 className="font-serif text-3xl">Message received.</h2>
              <p className="mt-4 text-[#555]">Thanks — we'll be in touch within one business day.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Send a message</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] font-mono tracking-[0.3em] uppercase">Name</label>
                  <input required value={f.name} onChange={set("name")} data-testid="contact-name" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
                </div>
                <div>
                  <label className="text-[10px] font-mono tracking-[0.3em] uppercase">Email</label>
                  <input required type="email" value={f.email} onChange={set("email")} data-testid="contact-email" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.3em] uppercase">Phone (optional)</label>
                <input value={f.phone} onChange={set("phone")} data-testid="contact-phone" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.3em] uppercase">Subject</label>
                <input required value={f.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" placeholder="How can we help?" />
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.3em] uppercase">Message</label>
                <textarea required rows={5} value={f.details} onChange={set("details")} data-testid="contact-details" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent resize-none" />
              </div>
              <button type="submit" disabled={submitting} data-testid="contact-submit" className="bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-10 py-4 text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-50">
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>

      {mapsUrl && (
        <div className="mt-20" data-testid="contact-map">
          <div className="aspect-[16/7] w-full overflow-hidden border border-[#E5E5E5]">
            <iframe src={mapsUrl} title="Location" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      )}
    </section>
  );
}
